import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDateForInput } from "../../utils/dateUtils";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Grid,
  IconButton,
  Divider,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Autocomplete,
  Avatar,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import ApplySchemeCreditDialog from "../../components/ApplySchemeCreditDialog";

export default function EditBill() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const billId = searchParams.get("id");
  const billedBy = localStorage.getItem("fullName") || "Unknown User";

  const [billData, setBillData] = useState({
    tokenNo: "",
    customerName: "",
    contact: "",
    advance: "",
    wasCustomizationGood: "",
    orderDescription: "",
    dueDate: "",
    discountType: "item", // 'item' or 'bill'
    billDiscountPercent: "", // Bill-level discount percentage
    couponCode: "", // Auto-generated coupon code
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedProducts, setSelectedProducts] = useState({}); // Store selected product details by item.id
  const [productSearch, setProductSearch] = useState({});
  const [productInputValues, setProductInputValues] = useState({});
  const [imagePreview, setImagePreview] = useState({ open: false, url: "" });
  const [rawMaterials, setRawMaterials] = useState([]); // Store raw materials for customizations
  const [calculations, setCalculations] = useState({
    subTotal: 0,
    totalTax: 0,
    totalDiscount: 0,
    totalAmount: 0,
    balance: 0,
  });
  const [schemeCreditDialogOpen, setSchemeCreditDialogOpen] = useState(false);
  const [appliedSchemeCredit, setAppliedSchemeCredit] = useState(0);
  const [availableSchemeCredit, setAvailableSchemeCredit] = useState(0);
  const [schemeCreditInfo, setSchemeCreditInfo] = useState(null);
  const [customerFetched, setCustomerFetched] = useState(false);
  const [originalCustomerId, setOriginalCustomerId] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(0);
  const [availableCoupon, setAvailableCoupon] = useState(0);

  const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

  useEffect(() => {
    if (billId) {
      fetchBillData(billId);
    } else {
      toast.error("No bill ID provided");
      navigate("/bills");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId]);

  useEffect(() => {
    calculateTotals();
  }, [items, billData.advance, billData.discountType, billData.billDiscountPercent, appliedSchemeCredit, appliedCoupon]);

  useEffect(() => {
    fetchRawMaterials();
  }, []);

  // Removed fetchAllProducts - we now search on-demand for better performance

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRawMaterials(data.materials || []);
      }
    } catch (error) {
      console.error("Error fetching raw materials:", error);
    }
  };

  const fetchBillData = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/bills/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to fetch bill");
      
      const data = await response.json();
      await populateFormWithBillData(data.bill);
    } catch (error) {
      console.error("Error fetching bill:", error);
      toast.error("Failed to load bill details");
      navigate("/bills");
    } finally {
      setLoading(false);
    }
  };

  const populateFormWithBillData = async (bill) => {
    // Set basic bill data
    setBillData({
      tokenNo: bill.CustomerId || "", // CustomerId now stores TokenNo
      customerName: bill.CustomerName,
      contact: bill.PhoneNumber || "",
      advance: bill.Advance.toString(),
      wasCustomizationGood: bill.WasCustomizationGood,
      orderDescription: bill.OrderDescription || "",
      dueDate: formatDateForInput(bill.DueDate),
      discountType: bill.DiscountType || 'item',
      billDiscountPercent: bill.DiscountType === 'bill' ? (bill.DiscountPercent || 0).toString() : '',
      couponCode: bill.CouponCode || "N/A", // Display existing coupon code
    });
    
    // Store original customer ID and set as fetched
    setOriginalCustomerId(bill.CustomerId || "");
    setCustomerFetched(true);
    
    // Set scheme credit
    setAppliedSchemeCredit(parseFloat(bill.SchemeCreditApplied) || 0);
    
    // Set coupon discount
    setAppliedCoupon(parseFloat(bill.CouponDiscount) || 0);
    
    // Fetch available scheme credit for this customer
    if (bill.CustomerId) {
      fetchAvailableSchemeCredit(bill.CustomerId);
    }
    
    // Transform items from database format to form format
    const { items: formItems, inputValues } = transformBillItemsToFormFormat(bill.items);
    setItems(formItems);
    setProductInputValues(inputValues);
    
    // Fetch product details for all products in the bill to populate selectedProducts
    await fetchProductDetailsForBillItems(formItems);
  };

  const transformBillItemsToFormFormat = (dbItems) => {
    // Group items by ProductCode since DB has one row per size
    const itemsMap = new Map();
    const inputValues = {};
    
    dbItems.forEach(dbItem => {
      if (!itemsMap.has(dbItem.ProductCode)) {
        const itemId = Date.now() + Math.random();
        itemsMap.set(dbItem.ProductCode, {
          id: itemId,
          productCode: dbItem.ProductCode,
          sizes: [],
        });
        // Store initial input value for this item
        inputValues[itemId] = dbItem.ProductCode;
      }
      
      const item = itemsMap.get(dbItem.ProductCode);
      
      // Extract customizations for this specific size with material information
      const alterations = dbItem.customizations
        ?.filter(c => c.Type === "Alterations")
        .map(c => ({ 
          description: c.Description, 
          cost: c.Cost.toString(),
          materialId: c.MaterialId || "",
          quantityUsed: c.QuantityUsed ? c.QuantityUsed.toString() : ""
        })) || [];
      
      const stitching = dbItem.customizations
        ?.filter(c => c.Type === "Stitching")
        .map(c => ({ 
          description: c.Description, 
          cost: c.Cost.toString(),
          materialId: c.MaterialId || "",
          quantityUsed: c.QuantityUsed ? c.QuantityUsed.toString() : ""
        })) || [];
      
      // Add size with its specific customizations
      item.sizes.push({
        size: dbItem.Size,
        quantity: dbItem.Quantity.toString(),
        unitPrice: dbItem.UnitPrice.toString(),
        discount: dbItem.Discount ? dbItem.Discount.toString() : "",
        alterations: alterations.length > 0 ? alterations : [{ description: "", cost: "", materialId: "", quantityUsed: "" }],
        stitching: stitching.length > 0 ? stitching : [{ description: "", cost: "", materialId: "", quantityUsed: "" }],
      });
    });
    
    // Return both items and input values
    return {
      items: Array.from(itemsMap.values()),
      inputValues
    };
  };

  const fetchProductDetailsForBillItems = async (formItems) => {
    try {
      const token = localStorage.getItem("token");
      const productDetailsMap = {};
      
      // Fetch product details for each unique product code
      for (const item of formItems) {
        if (item.productCode) {
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/inventory/products/${encodeURIComponent(item.productCode)}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.ok) {
              const data = await response.json();
              productDetailsMap[item.id] = data.product;
            }
          } catch (error) {
            console.error(`Error fetching product details for ${item.productCode}:`, error);
          }
        }
      }
      
      // Update selectedProducts state
      setSelectedProducts(productDetailsMap);
    } catch (error) {
      console.error("Error fetching product details for bill items:", error);
    }
  };

  const handleProductSearch = async (itemId, searchValue) => {
    // Update the input value
    setProductInputValues(prev => ({ ...prev, [itemId]: searchValue }));
    
    if (!searchValue || searchValue.length < 2) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/inventory/products/search/${encodeURIComponent(searchValue)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProductSearch((prev) => ({ ...prev, [itemId]: data.products || [] }));
      }
    } catch (error) {
      console.error("Error searching products:", error);
    }
  };

  const handleProductSelect = async (itemId, product) => {
    if (!product) return;
    
    // If product is a string (manual input), fetch product details
    if (typeof product === 'string') {
      setProductInputValues(prev => ({ ...prev, [itemId]: product }));
      
      // Try to fetch product details for the manually entered code
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/api/inventory/products/search/${encodeURIComponent(product)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const data = await response.json();
          const foundProduct = data.products?.find(p => p.ProductCode === product);
          if (foundProduct) {
            setSelectedProducts(prev => ({ ...prev, [itemId]: foundProduct }));
            
            // Update items with the found product details
            // Get first available size with stock, or first size if none have stock
            let firstSize = "";
            if (foundProduct.sizes && foundProduct.sizes.length > 0) {
              const sizeWithStock = foundProduct.sizes.find(s => s.QuantityInStock > 0);
              firstSize = sizeWithStock ? sizeWithStock.Size : foundProduct.sizes[0].Size;
            }
            
            setItems(items.map((item) => {
              if (item.id === itemId) {
                const updatedSizes = item.sizes.map(s => ({
                  size: firstSize, // Reset to first available size with stock
                  quantity: s.quantity || "1", // Preserve quantity
                  unitPrice: foundProduct.Price || "", // Update to new product's price
                  discount: s.discount || "", // Preserve discount
                  alterations: [{ description: "", cost: "", materialId: "", quantityUsed: "" }], // Clear customizations
                  stitching: [{ description: "", cost: "", materialId: "", quantityUsed: "" }], // Clear customizations
                }));
                
                return { ...item, productCode: foundProduct.ProductCode, sizes: updatedSizes };
              }
              return item;
            }));
          } else {
            // Product not found, just update the code
            setItems(items.map((item) => 
              item.id === itemId ? { ...item, productCode: product } : item
            ));
          }
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
        // On error, just update the product code
        setItems(items.map((item) => 
          item.id === itemId ? { ...item, productCode: product } : item
        ));
      }
      return;
    }
    
    // If product is an object (selected from dropdown), store it and auto-fill details
    setSelectedProducts(prev => ({ ...prev, [itemId]: product }));
    setProductInputValues(prev => ({ ...prev, [itemId]: product.ProductCode }));
    
    // Get first available size with stock, or first size if none have stock
    let firstSize = "";
    if (product.sizes && product.sizes.length > 0) {
      const sizeWithStock = product.sizes.find(s => s.QuantityInStock > 0);
      firstSize = sizeWithStock ? sizeWithStock.Size : product.sizes[0].Size;
    }
    
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          // When product changes, update all sizes with new product details
          const updatedSizes = item.sizes.map(s => ({
            size: firstSize, // Reset to first available size with stock
            quantity: s.quantity || "1", // Preserve existing quantity or default to 1
            unitPrice: product.Price || "", // Always update to new product's price
            discount: s.discount || "", // Preserve discount
            alterations: [{ description: "", cost: "" }], // Clear customizations
            stitching: [{ description: "", cost: "" }], // Clear customizations
          }));
          
          return { 
            ...item, 
            productCode: product.ProductCode,
            sizes: updatedSizes
          };
        }
        return item;
      })
    );
  };

  const handleImagePreview = (url) => {
    setImagePreview({ open: true, url });
  };

  const closeImagePreview = () => {
    setImagePreview({ open: false, url: "" });
  };

  const calculateTotals = () => {
    let subTotal = 0;
    let itemLevelDiscount = 0;

    items.forEach((item) => {
      item.sizes.forEach((sizeItem) => {
        const sizeSubTotal = (parseFloat(sizeItem.quantity) || 0) * (parseFloat(sizeItem.unitPrice) || 0);
        
        const alterationsTotal = sizeItem.alterations.reduce(
          (sum, a) => sum + (parseFloat(a.cost) || 0),
          0
        );
        const stitchingTotal = sizeItem.stitching.reduce(
          (sum, s) => sum + (parseFloat(s.cost) || 0),
          0
        );

        // SubTotal should be BEFORE discount
        const sizeTotal = sizeSubTotal + alterationsTotal + stitchingTotal;
        subTotal += sizeTotal;
        
        // Only add item-level discounts if discount type is 'item'
        if (billData.discountType === 'item') {
          itemLevelDiscount += (parseFloat(sizeItem.discount) || 0);
        }
      });
    });

    // Calculate total discount based on discount type
    let totalDiscount = 0;
    if (billData.discountType === 'bill') {
      // Calculate discount from percentage
      const discountPercent = parseFloat(billData.billDiscountPercent) || 0;
      totalDiscount = (subTotal * discountPercent) / 100;
    } else {
      totalDiscount = itemLevelDiscount;
    }

    // Total Amount is SubTotal minus Discount minus Coupon
    const totalAmount = subTotal - totalDiscount - appliedCoupon;
    const balance = totalAmount - (parseFloat(billData.advance) || 0) - appliedSchemeCredit;

    setCalculations({
      subTotal: subTotal.toFixed(2),
      totalTax: "0.00",
      totalDiscount: totalDiscount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      balance: balance.toFixed(2),
    });
  };

  // Fetch customer's available coupon (including the amount used in this bill)
  const fetchCustomerCoupon = async (tokenNo, billCouponUsed = 0) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/${tokenNo}/coupon`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const currentCouponAmount = parseFloat(data.couponAmount) || 0;
        // Restore original coupon amount by adding back what was used in this bill
        const originalCouponAmount = currentCouponAmount + billCouponUsed;
        setAvailableCoupon(originalCouponAmount);
        // console.log(`🎫 EditBill: Current coupon: ₹${currentCouponAmount}, Used in this bill: ₹${billCouponUsed}, Available for editing: ₹${originalCouponAmount}`);
      } else {
        setAvailableCoupon(0);
      }
    } catch (error) {
      console.error("Error fetching customer coupon:", error);
      setAvailableCoupon(0);
    }
  };
  const fetchAvailableSchemeCredit = async (tokenNo) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/schemes/customer/${tokenNo}/available-credit`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const totalAvailable = data.schemes?.reduce((sum, s) => sum + parseFloat(s.AvailableCredit), 0) || 0;
        setAvailableSchemeCredit(totalAvailable);
      }
    } catch (error) {
      console.error("Error fetching available scheme credit:", error);
    }
  };

  // Handle scheme credit application success
  const handleSchemeCreditApplied = (data) => {
    // Update applied scheme credit
    setAppliedSchemeCredit(data.appliedAmount || 0);
    
    // Refresh available credit
    if (billData.tokenNo) {
      fetchAvailableSchemeCredit(billData.tokenNo);
    }
    
    // Don't show toast here - will show when bill is saved
  };

  const handleBillDataChange = (e) => {
    const { name, value } = e.target;
    setBillData({ ...billData, [name]: value });
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(items.map((item) => (item.id === itemId ? { ...item, [field]: value } : item)));
  };

  const handleSizeChange = (itemId, sizeIndex, field, value) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          newSizes[sizeIndex] = { ...newSizes[sizeIndex], [field]: value };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const handleCustomizationChange = (itemId, sizeIndex, type, customIndex, field, value) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          const newCustomizations = [...newSizes[sizeIndex][type]];
          newCustomizations[customIndex] = { ...newCustomizations[customIndex], [field]: value };
          newSizes[sizeIndex] = { ...newSizes[sizeIndex], [type]: newCustomizations };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const addSize = (itemId) => {
    setItems(
      items.map((item) =>
        item.id === itemId
          ? { 
              ...item, 
              sizes: [
                ...item.sizes, 
                { 
                  size: "", 
                  quantity: "", 
                  unitPrice: "",
                  discount: "",
                  alterations: [{ description: "", cost: "" }],
                  stitching: [{ description: "", cost: "" }]
                }
              ] 
            }
          : item
      )
    );
  };

  const removeSize = (itemId, sizeIndex) => {
    setItems(
      items.map((item) =>
        item.id === itemId ? { ...item, sizes: item.sizes.filter((_, i) => i !== sizeIndex) } : item
      )
    );
  };

  const addCustomization = (itemId, sizeIndex, type) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          newSizes[sizeIndex] = {
            ...newSizes[sizeIndex],
            [type]: [...newSizes[sizeIndex][type], { description: "", cost: "", materialId: "", quantityUsed: "" }]
          };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const removeCustomization = (itemId, sizeIndex, type, customIndex) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          newSizes[sizeIndex] = {
            ...newSizes[sizeIndex],
            [type]: newSizes[sizeIndex][type].filter((_, i) => i !== customIndex)
          };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const validateCurrentItem = () => {
    const lastItem = items[items.length - 1];
    if (!lastItem.productCode) {
      setErrors({ general: "Please enter Product Code before adding new item" });
      return false;
    }
    if (lastItem.sizes.some((s) => !s.size || !s.unitPrice)) {
      setErrors({ general: "Please fill all size details before adding new item" });
      return false;
    }
    setErrors({});
    return true;
  };

  const addNewItem = () => {
    if (!validateCurrentItem()) return;

    const newItemId = Date.now();
    setItems([
      ...items,
      {
        id: newItemId,
        productCode: "",
        sizes: [
          { 
            size: "", 
            quantity: "", 
            unitPrice: "",
            alterations: [{ description: "", cost: "" }],
            stitching: [{ description: "", cost: "" }]
          }
        ],
      },
    ]);
    
    // Initialize input value for new item
    setProductInputValues(prev => ({ ...prev, [newItemId]: "" }));
  };

  const removeItem = (itemId) => {
    if (items.length > 1) {
      setItemToDelete(itemId);
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeleteItem = () => {
    if (itemToDelete) {
      setItems(items.filter((item) => item.id !== itemToDelete));
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const cancelDeleteItem = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleSaveChanges = (e) => {
    e.preventDefault();

    if (!validateCurrentItem()) return;

    if (!billData.customerName) {
      setErrors({ general: "Customer name is required" });
      return;
    }

    setSaveDialogOpen(true);
  };

  const confirmSave = async () => {
    setSaveDialogOpen(false);
    
    const billPayload = {
      tokenNo: billData.tokenNo,
      customerName: billData.customerName,
      contact: billData.contact,
      advance: billData.advance,
      wasCustomizationGood: billData.wasCustomizationGood,
      orderDescription: billData.orderDescription,
      dueDate: billData.dueDate,
      taxRate: billData.taxRate || 0,
      discount: billData.discountType === 'bill' ? parseFloat(calculations.totalDiscount) : 0,
      discountPercent: billData.discountType === 'bill' ? (billData.billDiscountPercent || 0) : 0,
      discountType: billData.discountType,
      items: items.map((item) => ({
        productCode: item.productCode,
        sizes: item.sizes.map(size => ({
          ...size,
          // Only include item discount if discount type is 'item'
          discount: billData.discountType === 'item' ? (size.discount || 0) : 0,
          alterations: size.alterations.filter((a) => a.description && a.description.trim() !== ''),
          stitching: size.stitching.filter((s) => s.description && s.description.trim() !== ''),
        })),
      })),
      ...calculations,
    };

    try {
      const token = localStorage.getItem("token");
      
      // Update the bill first
      const response = await fetch(`${API_BASE_URL}/api/bills/${billId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(billPayload),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        throw new Error("Failed to parse server response");
      }

      if (response.ok) {
        // Update scheme credit if it changed
        try {
          const creditResponse = await fetch(`${API_BASE_URL}/api/schemes/credit/bill/${billId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              newAmount: appliedSchemeCredit,
              notes: schemeCreditInfo?.notes || `Updated for Bill #${data.billNumber || billId}`
            }),
          });

          if (creditResponse.ok) {
            toast.success("Bill updated successfully!");
          } else {
            // If credit update fails, still show success for bill but warn about credit
            toast.warning("Bill updated but failed to update scheme credit. Please check manually.");
          }
        } catch (creditError) {
          console.error("Error updating scheme credit:", creditError);
          // If credit update fails, still show success for bill but warn about credit
          toast.warning("Bill updated but failed to update scheme credit. Please check manually.");
        }
        
        // Dispatch event to refresh customer data (for coupon updates)
        // Trigger data refresh events
        setTimeout(() => {
          window.dispatchEvent(new Event('refreshCustomerData'));
          window.dispatchEvent(new Event('refreshDashboardData'));
        }, 500);
        
        navigate("/bills");
      } else {
        // Handle error response - show specific error message from backend
        const errorMsg = data.error || data.message || "Failed to update bill";
        toast.error(errorMsg);
        setErrors({ general: errorMsg });
      }
    } catch (error) {
      console.error("Error updating bill:", error);
      const errorMsg = error.message || "Failed to update bill. Please try again.";
      toast.error(errorMsg);
      setErrors({ general: errorMsg });
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "60vh",
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
        }}>
          <CircularProgress sx={{ color: "#c59a58" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ 
        p: { xs: 2, md: 4 }, 
        minHeight: "calc(100vh - 80px)",
        // background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
      }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/bills")}
          sx={{
            color: "#c59a58",
            mb: 3,
            "&:hover": { bgcolor: "rgba(197, 154, 88, 0.1)" },
          }}
        >
          Back to Bills
        </Button>

        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              letterSpacing: { xs: 1, sm: 2, md: 3 },
              fontSize: { xs: "1rem", sm: "1.5rem", md: "1.5rem" },
              color: "#c59a58",
              textDecoration: "underline",
              textUnderlineOffset: { xs: 6, sm: 12 },
              textDecorationThickness: 2,
              wordBreak: "break-word",
            }}
          >
            EDIT BILL
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors({})}>
              {errors.general}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSaveChanges}>
            {/* CUSTOMER DETAILS */}
            <Paper elevation={8} sx={{ 
              mb: 3,
              p: { xs: 1.5, sm: 2, md: 3 }, 
              borderRadius: 2,
              background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
              border: "1px solid #444"
            }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#c59a58",
                  mb: 2,
                  fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                  letterSpacing: 0.5,
                }}
              >
                Customer Details (Read-Only)
              </Typography>
              <Alert severity="info" sx={{ mb: 2, bgcolor: "rgba(33, 150, 243, 0.1)", color: "#90caf9" }}>
                Customer details cannot be changed. Create a new bill to change customer.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Token No"
                    value={billData.tokenNo || "N/A"}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&.Mui-disabled": {
                          backgroundColor: "#2a2a2a",
                          color: "#888",
                        },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Customer Name"
                    value={billData.customerName}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&.Mui-disabled": {
                          backgroundColor: "#2a2a2a",
                          color: "#888",
                        },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Phone Number"
                    value={billData.contact}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&.Mui-disabled": {
                          backgroundColor: "#2a2a2a",
                          color: "#888",
                        },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    label="Billed By" 
                    value={billedBy} 
                    fullWidth 
                    disabled 
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&.Mui-disabled": {
                          backgroundColor: "#2a2a2a",
                          color: "#888",
                        },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Coupon Code"
                    value={billData.couponCode}
                    fullWidth
                    disabled
                    InputProps={{ 
                      readOnly: true,
                      style: { backgroundColor: '#2a2a2a', color: '#888' }
                    }}
                    helperText="Auto-generated code (cannot be changed)"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&.Mui-disabled": {
                          backgroundColor: "#2a2a2a",
                          color: "#888",
                        },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                      "& .MuiFormHelperText-root": { color: "#888" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Advance Amount (₹)"
                    name="advance"
                    type="text"
                    value={billData.advance}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        handleBillDataChange(e);
                      }
                    }}
                    placeholder="Enter advance amount"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&:hover fieldset": { borderColor: "#c59a58" },
                        "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                    }}
                  />
                </Grid>
                {billData.tokenNo && customerFetched && (
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setSchemeCreditDialogOpen(true)}
                      sx={{
                        height: '56px',
                        borderColor: '#c59a58',
                        color: '#c59a58',
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#b68649',
                          bgcolor: 'rgba(197, 154, 88, 0.1)'
                        }
                      }}
                    >
                      {appliedSchemeCredit > 0 
                        ? `Applied: ₹${appliedSchemeCredit.toFixed(2)}`
                        : 'Apply Scheme Credit'}
                    </Button>
                    {availableSchemeCredit > 0 && appliedSchemeCredit === 0 && (
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: "#888" }}>
                        Available: ₹{availableSchemeCredit.toFixed(2)}
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Due Date"
                    name="dueDate"
                    type="date"
                    value={billData.dueDate}
                    onChange={handleBillDataChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&:hover fieldset": { borderColor: "#c59a58" },
                        "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Order Description"
                    name="orderDescription"
                    value={billData.orderDescription}
                    onChange={handleBillDataChange}
                    placeholder="Add any comments or special instructions for this order"
                    fullWidth
                    multiline
                    rows={3}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&:hover fieldset": { borderColor: "#c59a58" },
                        "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* ITEMS */}
            {items.map((item, itemIndex) => (
              <Paper key={item.id} elevation={8} sx={{ 
                mb: 3,
                p: { xs: 1.5, sm: 2, md: 3 }, 
                borderRadius: 2,
                background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                border: "1px solid #444"
              }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#c59a58",
                      fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                      letterSpacing: 0.5,
                    }}
                  >
                    Item #{itemIndex + 1}
                  </Typography>
                  {items.length > 1 && (
                    <IconButton color="error" onClick={() => removeItem(item.id)} sx={{ color: "#ff6b6b" }}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Autocomplete
                  options={productSearch[item.id] || []}
                  getOptionLabel={(option) => `${option.ProductCode} - ${option.ProductName}`}
                  inputValue={productInputValues[item.id] || ""}
                  onChange={(e, newValue) => handleProductSelect(item.id, newValue)}
                  onInputChange={(e, value) => handleProductSearch(item.id, value)}
                  filterOptions={(x) => x}
                  noOptionsText="Type at least 2 characters to search..."
                  freeSolo
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Product" 
                      required 
                      fullWidth 
                      placeholder="Search by code, name, color, size, type, or supplier..."
                      helperText="Type at least 2 characters to search"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#333",
                          color: "#fff",
                          "& fieldset": { borderColor: "#555" },
                          "&:hover fieldset": { borderColor: "#c59a58" },
                          "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                        },
                        "& .MuiInputLabel-root": { color: "#c59a58" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                        "& .MuiFormHelperText-root": { color: "#888" },
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps} sx={{ display: "flex", alignItems: "center", gap: 2, bgcolor: "#2a2a2a", color: "#fff", "&:hover": { bgcolor: "#333" } }}>
                      <ListItemAvatar>
                        {option.Photo ? (
                          <Avatar
                            src={`${API_BASE_URL}${option.Photo}`}
                            variant="rounded"
                            sx={{ width: 50, height: 50, cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImagePreview(`${API_BASE_URL}${option.Photo}`);
                            }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 50, height: 50, bgcolor: "#444" }}>
                            <ImageIcon sx={{ color: "#c59a58" }} />
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${option.ProductCode} - ${option.ProductName}`}
                        secondary={`${option.Category} - ${option.Color || 'N/A'} - ₹${option.Price}${option.PurchaseFrom ? ` - Supplier: ${option.PurchaseFrom}` : ''}`}
                        sx={{
                          "& .MuiListItemText-primary": { color: "#fff" },
                          "& .MuiListItemText-secondary": { color: "#888" },
                        }}
                      />
                      {option.Photo && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImagePreview(`${API_BASE_URL}${option.Photo}`);
                          }}
                          sx={{ color: "#c59a58" }}
                        >
                          <ZoomInIcon />
                        </IconButton>
                      )}
                    </Box>
                    );
                  }}
                  sx={{ mb: 3 }}
                />

                {/* SIZES WITH CUSTOMIZATIONS */}
                <Typography sx={{ color: "#c59a58", fontWeight: 700, mb: 2, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                  Sizes
                </Typography>
                {item.sizes.map((sizeItem, sizeIndex) => (
                  <Box key={sizeIndex} sx={{ mb: 3, p: 2, border: "1px solid #555", borderRadius: 2, bgcolor: "#2a2a2a" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography sx={{ fontWeight: 600, color: "#c59a58", fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.95rem" } }}>
                        Size #{sizeIndex + 1}
                      </Typography>
                      {item.sizes.length > 1 && (
                        <IconButton size="small" color="error" onClick={() => removeSize(item.id, sizeIndex)} sx={{ color: "#ff6b6b" }}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          label="Size"
                          value={sizeItem.size}
                          onChange={(e) => handleSizeChange(item.id, sizeIndex, "size", e.target.value)}
                          fullWidth
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#333",
                              color: "#fff",
                              "& fieldset": { borderColor: "#555" },
                              "&:hover fieldset": { borderColor: "#c59a58" },
                              "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                            },
                            "& .MuiInputLabel-root": { color: "#c59a58" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            "& .MuiSelect-icon": { color: "#c59a58" },
                          }}
                        >
                          {(() => {
                            // Get the selected product details
                            const selectedProduct = selectedProducts[item.id];
                            
                            // For ready-made products, use sizes from purchase history (ProductSizes table)
                            // For customized products, use the configured sizes
                            let availableSizes = [];
                            
                            if (selectedProduct?.sizes && selectedProduct.sizes.length > 0) {
                              // Product has specific sizes from database
                              // In edit mode, show all sizes (not just those with stock) since user might be editing existing items
                              availableSizes = selectedProduct.sizes.map(s => s.Size);
                            } else if (selectedProduct?.ProductType) {
                              // Fallback to product-type specific sizes if no sizes in database
                              const productTypeSizes = {
                                'Salwar Suits': ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'],
                                'Bedsheets': ['Single', 'Double', 'Queen', 'King'],
                                'Footwear': ['5', '6', '7', '8', '9', '10', '11', '12'],
                                'Cosmetics': ['One Size'],
                                'Earrings': ['One Size'],
                                'Accessories': ['One Size', 'Small', 'Medium', 'Large'],
                                'Jewellery': ['XS', 'S', 'M', 'L', 'XL', 'Adjustable']
                              };
                              availableSizes = productTypeSizes[selectedProduct.ProductType] || sizes;
                            } else {
                              // Final fallback to generic sizes
                              availableSizes = sizes;
                            }
                            
                            return availableSizes.map((size) => (
                              <MenuItem key={size} value={size} sx={{ color: "#fff", bgcolor: "#333", "&:hover": { bgcolor: "#444" } }}>
                                {size}
                              </MenuItem>
                            ));
                          })()}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Q"
                          type="text"
                          value={sizeItem.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d+$/.test(value)) {
                              handleSizeChange(item.id, sizeIndex, "quantity", value);
                            }
                          }}
                          placeholder="Qty"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#333",
                              color: "#fff",
                              "& fieldset": { borderColor: "#555" },
                              "&:hover fieldset": { borderColor: "#c59a58" },
                              "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                            },
                            "& .MuiInputLabel-root": { color: "#c59a58" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="UP (₹)"
                          type="text"
                          value={sizeItem.unitPrice}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              handleSizeChange(item.id, sizeIndex, "unitPrice", value);
                            }
                          }}
                          placeholder="Price"
                          fullWidth
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#333",
                              color: "#fff",
                              "& fieldset": { borderColor: "#555" },
                              "&:hover fieldset": { borderColor: "#c59a58" },
                              "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                            },
                            "& .MuiInputLabel-root": { color: "#c59a58" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          label="D (₹)"
                          type="text"
                          value={sizeItem.discount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              handleSizeChange(item.id, sizeIndex, "discount", value);
                            }
                          }}
                          placeholder="Disc"
                          fullWidth
                          disabled={billData.discountType === 'bill'}
                          helperText={billData.discountType === 'bill' ? 'Bill-level D active' : ''}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#333",
                              color: "#fff",
                              "& fieldset": { borderColor: "#555" },
                              "&:hover fieldset": { borderColor: "#c59a58" },
                              "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                              "&.Mui-disabled": {
                                backgroundColor: "#2a2a2a",
                                color: "#888",
                              },
                            },
                            "& .MuiInputLabel-root": { color: "#c59a58" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            "& .MuiFormHelperText-root": { color: "#888" },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* ALTERATIONS FOR THIS SIZE */}
                    <Typography sx={{ color: "#c59a58", fontWeight: 600, mb: 1, mt: 2, fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.95rem" } }}>
                      Alterations (Optional)
                    </Typography>
                    {sizeItem.alterations.map((alt, altIndex) => (
                      <Grid container spacing={2} key={altIndex} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            label="Description"
                            value={alt.description}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "description", e.target.value)
                            }
                            placeholder="E.g., Sleeve shortening"
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            label="Cost (₹)"
                            type="text"
                            value={alt.cost}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "cost", value);
                              }
                            }}
                            placeholder="Cost"
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Material (Optional)"
                            value={alt.materialId || ""}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "materialId", e.target.value)
                            }
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                              "& .MuiSelect-icon": { color: "#c59a58" },
                            }}
                          >
                            <MenuItem value="" sx={{ color: "#fff", bgcolor: "#333", "&:hover": { bgcolor: "#444" } }}>None</MenuItem>
                            {rawMaterials.map((material) => (
                              <MenuItem key={material.MaterialId} value={material.MaterialId} sx={{ color: "#fff", bgcolor: "#333", "&:hover": { bgcolor: "#444" } }}>
                                {material.MaterialName} ({material.QuantityInStock} {material.Unit})
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={1.5}>
                          <TextField
                            label="Qty"
                            type="text"
                            value={alt.quantityUsed || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "quantityUsed", value);
                              }
                            }}
                            placeholder="0"
                            fullWidth
                            size="small"
                            disabled={!alt.materialId}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                                "&.Mui-disabled": {
                                  backgroundColor: "#2a2a2a",
                                  color: "#888",
                                },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={0.5}>
                          <IconButton size="small" color="error" onClick={() => removeCustomization(item.id, sizeIndex, "alterations", altIndex)} sx={{ color: "#ff6b6b" }}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addCustomization(item.id, sizeIndex, "alterations")}
                      size="small"
                      sx={{ mb: 2, color: "#c59a58", "&:hover": { bgcolor: "rgba(197, 154, 88, 0.1)" } }}
                    >
                      Add Alteration
                    </Button>

                    {/* STITCHING FOR THIS SIZE */}
                    <Typography sx={{ color: "#c59a58", fontWeight: 600, mb: 1, mt: 2, fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.95rem" } }}>
                      Stitching (Optional)
                    </Typography>
                    {sizeItem.stitching.map((stitch, stitchIndex) => (
                      <Grid container spacing={2} key={stitchIndex} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            label="Description"
                            value={stitch.description}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "description", e.target.value)
                            }
                            placeholder="E.g., Custom fitting"
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            label="Cost (₹)"
                            type="text"
                            value={stitch.cost}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "cost", value);
                              }
                            }}
                            placeholder="Cost"
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Material (Optional)"
                            value={stitch.materialId || ""}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "materialId", e.target.value)
                            }
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                              "& .MuiSelect-icon": { color: "#c59a58" },
                            }}
                          >
                            <MenuItem value="" sx={{ color: "#fff", bgcolor: "#333", "&:hover": { bgcolor: "#444" } }}>None</MenuItem>
                            {rawMaterials.map((material) => (
                              <MenuItem key={material.MaterialId} value={material.MaterialId} sx={{ color: "#fff", bgcolor: "#333", "&:hover": { bgcolor: "#444" } }}>
                                {material.MaterialName} ({material.QuantityInStock} {material.Unit})
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={1.5}>
                          <TextField
                            label="Qty"
                            type="text"
                            value={stitch.quantityUsed || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "quantityUsed", value);
                              }
                            }}
                            placeholder="0"
                            fullWidth
                            size="small"
                            disabled={!stitch.materialId}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#333",
                                color: "#fff",
                                "& fieldset": { borderColor: "#555" },
                                "&:hover fieldset": { borderColor: "#c59a58" },
                                "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                                "&.Mui-disabled": {
                                  backgroundColor: "#2a2a2a",
                                  color: "#888",
                                },
                              },
                              "& .MuiInputLabel-root": { color: "#c59a58" },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={0.5}>
                          <IconButton size="small" color="error" onClick={() => removeCustomization(item.id, sizeIndex, "stitching", stitchIndex)} sx={{ color: "#ff6b6b" }}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addCustomization(item.id, sizeIndex, "stitching")}
                      size="small"
                      sx={{ mb: 2, color: "#c59a58", "&:hover": { bgcolor: "rgba(197, 154, 88, 0.1)" } }}
                    >
                      Add Stitching
                    </Button>
                  </Box>
                ))}
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={() => addSize(item.id)} 
                  size="small" 
                  sx={{ mb: 2, color: "#c59a58", "&:hover": { bgcolor: "rgba(197, 154, 88, 0.1)" } }}
                >
                  Add Another Size
                </Button>

              </Paper>
            ))}

            {/* ADD ITEM BUTTON */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addNewItem}
              fullWidth
              sx={{ 
                mb: 3, 
                py: 1.5, 
                borderWidth: 2, 
                borderColor: "#c59a58",
                color: "#c59a58",
                "&:hover": { 
                  borderWidth: 2, 
                  borderColor: "#b68649",
                  bgcolor: "rgba(197, 154, 88, 0.1)"
                }
              }}
            >
              Add Another Item
            </Button>

            {/* CUSTOMIZATION FEEDBACK */}
            <Paper elevation={8} sx={{ 
              mb: 3,
              p: { xs: 1.5, sm: 2, md: 3 }, 
              borderRadius: 2,
              background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
              border: "1px solid #444"
            }}>
              <FormControl component="fieldset">
                <FormLabel sx={{ color: "#c59a58", fontWeight: 600 }}>Was customization good?</FormLabel>
                <RadioGroup
                  row
                  name="wasCustomizationGood"
                  value={billData.wasCustomizationGood}
                  onChange={handleBillDataChange}
                >
                  <FormControlLabel 
                    value="Yes" 
                    control={<Radio sx={{ color: "#c59a58", "&.Mui-checked": { color: "#c59a58" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>Yes</Typography>} 
                  />
                  <FormControlLabel 
                    value="No" 
                    control={<Radio sx={{ color: "#c59a58", "&.Mui-checked": { color: "#c59a58" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>No</Typography>} 
                  />
                </RadioGroup>
              </FormControl>
            </Paper>

            {/* DISCOUNT TYPE SELECTOR */}
            <Paper elevation={8} sx={{ 
              mb: 3,
              p: { xs: 1.5, sm: 2, md: 3 }, 
              borderRadius: 2,
              background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
              border: "1px solid #444"
            }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#c59a58",
                  mb: 2,
                  fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                  letterSpacing: 0.5,
                }}
              >
                D Options
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ color: "#c59a58", fontWeight: 600 }}>Apply D On:</FormLabel>
                <RadioGroup
                  row
                  name="discountType"
                  value={billData.discountType}
                  onChange={handleBillDataChange}
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel 
                    value="item" 
                    control={<Radio sx={{ color: "#c59a58", "&.Mui-checked": { color: "#c59a58" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>Individual Items</Typography>} 
                  />
                  <FormControlLabel 
                    value="bill" 
                    control={<Radio sx={{ color: "#c59a58", "&.Mui-checked": { color: "#c59a58" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>Total Bill</Typography>} 
                  />
                </RadioGroup>
                
                {billData.discountType === 'bill' && (
                  <TextField
                    label="Bill D Percentage (%)"
                    name="billDiscountPercent"
                    type="text"
                    value={billData.billDiscountPercent}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow numbers between 0-100 with decimals
                      if (value === "" || (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100)) {
                        handleBillDataChange(e);
                      }
                    }}
                    placeholder="Enter d percentage (0-100)"
                    fullWidth
                    helperText={`D will be calculated as ${billData.billDiscountPercent || 0}% of subtotal`}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#333",
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                        "&:hover fieldset": { borderColor: "#c59a58" },
                        "&.Mui-focused fieldset": { borderColor: "#c59a58" },
                      },
                      "& .MuiInputLabel-root": { color: "#c59a58" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#c59a58" },
                      "& .MuiFormHelperText-root": { color: "#888" },
                    }}
                  />
                )}
                
                {billData.discountType === 'item' && (
                  <Alert severity="info" sx={{ mt: 1, bgcolor: "rgba(33, 150, 243, 0.1)", color: "#90caf9" }}>
                    Add d to individual items in the "D (₹)" field for each size
                  </Alert>
                )}
              </FormControl>
            </Paper>

            {/* BILL SUMMARY */}
            <Paper elevation={8} sx={{ 
              mb: 3,
              p: { xs: 1.5, sm: 2, md: 3 }, 
              borderRadius: 2,
              background: "rgba(0, 0, 0, 0.4)",
              border: "2px solid rgba(196, 165, 116, 0.3)"
            }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#C4A574",
                  mb: 3,
                  fontSize: "1.1rem",
                  letterSpacing: 0.5,
                  textAlign: "center",
                }}
              >
                Bill Summary
              </Typography>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography>SubTotal:</Typography>
                <Typography fontWeight={600}>₹ {calculations.subTotal}</Typography>
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography>Tax:</Typography>
                <Typography fontWeight={600}>₹ {calculations.totalTax}</Typography>
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography>D {billData.discountType === 'bill' ? '(Bill Level)' : '(Item Level)'}:</Typography>
                <Typography fontWeight={600}>- ₹ {calculations.totalDiscount}</Typography>
              </Box>
              
              {appliedCoupon > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#ff9800" }}>
                  <Typography sx={{ fontWeight: 600 }}>Coupon Applied:</Typography>
                  <Typography sx={{ fontWeight: 600 }}>- ₹ {appliedCoupon.toFixed(2)}</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2, borderColor: "rgba(196, 165, 116, 0.2)" }} />
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography variant="h6">Total Amount:</Typography>
                <Typography variant="h6" fontWeight={700}>₹ {calculations.totalAmount}</Typography>
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography>Advance Paid:</Typography>
                <Typography>- ₹ {billData.advance || 0}</Typography>
              </Box>
              
              {appliedSchemeCredit > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#9c27b0" }}>
                  <Typography sx={{ fontWeight: 600 }}>Scheme Credit Applied:</Typography>
                  <Typography sx={{ fontWeight: 600 }}>- ₹ {appliedSchemeCredit.toFixed(2)}</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2, borderColor: "rgba(196, 165, 116, 0.2)" }} />
              
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: "#C4A574",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                }}
              >
                <Typography>Balance Due:</Typography>
                <Typography>₹ {calculations.balance}</Typography>
              </Box>
            </Paper>

            {/* ACTION BUTTONS */}
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate("/bills")} 
                size="large"
                sx={{
                  borderColor: "#c59a58",
                  color: "#c59a58",
                  "&:hover": {
                    borderColor: "#b68649",
                    bgcolor: "rgba(197, 154, 88, 0.1)"
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                type="submit" 
                size="large" 
                sx={{ 
                  px: 4,
                  bgcolor: "#c59a58",
                  color: "#000",
                  "&:hover": { bgcolor: "#b68649" }
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>

          {/* Save Confirmation Dialog */}
          <Dialog
            open={saveDialogOpen}
            onClose={() => setSaveDialogOpen(false)}
            PaperProps={{
              sx: {
                background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                border: "1px solid #444",
                borderRadius: 2,
              }
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
              borderBottom: '2px solid #C4A574',
              color: '#fff',
              pb: 2
            }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#C4A574' }}>
                Confirm Changes
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, bgcolor: 'transparent' }}>
              <DialogContentText sx={{ color: '#fff' }}>
                Are you sure you want to save changes to this bill? This will update the existing bill record.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ 
              p: 2, 
              background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
              borderTop: '1px solid #444',
              gap: 1
            }}>
              <Button 
                onClick={() => setSaveDialogOpen(false)}
                variant="outlined"
                sx={{
                  color: '#C4A574',
                  borderColor: '#C4A574',
                  '&:hover': {
                    bgcolor: 'rgba(196, 165, 116, 0.1)',
                    borderColor: '#C4A574'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmSave} 
                variant="contained" 
                sx={{ 
                  bgcolor: "#C4A574",
                  color: "#000",
                  "&:hover": { bgcolor: "#b68649" }
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Product Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={cancelDeleteItem}
            PaperProps={{
              sx: {
                background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                border: "1px solid #444",
                borderRadius: 2,
              }
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
              borderBottom: '2px solid #ff6b6b',
              color: '#fff',
              pb: 2
            }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#ff6b6b' }}>
                Confirm Delete
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, bgcolor: 'transparent' }}>
              <DialogContentText sx={{ color: '#fff' }}>
                Are you sure you want to remove this product from the bill? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ 
              p: 2, 
              background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
              borderTop: '1px solid #444',
              gap: 1
            }}>
              <Button 
                onClick={cancelDeleteItem}
                variant="outlined"
                sx={{
                  color: '#C4A574',
                  borderColor: '#C4A574',
                  '&:hover': {
                    bgcolor: 'rgba(196, 165, 116, 0.1)',
                    borderColor: '#C4A574'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDeleteItem} 
                variant="contained" 
                sx={{ 
                  bgcolor: "#ff6b6b",
                  color: "#fff",
                  "&:hover": { bgcolor: "#ff5252" }
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* IMAGE PREVIEW DIALOG */}
          <Dialog 
            open={imagePreview.open} 
            onClose={closeImagePreview} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: {
                background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                border: "1px solid #444",
                borderRadius: 2,
              }
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
              borderBottom: '2px solid #C4A574',
              color: '#fff',
              pb: 2
            }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#C4A574' }}>
                Product Image
              </Typography>
              <IconButton
                onClick={closeImagePreview}
                sx={{ color: "#C4A574" }}
              >
                <DeleteIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, bgcolor: 'transparent' }}>
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <img
                  src={imagePreview.url}
                  alt="Product"
                  style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
                />
              </Box>
            </DialogContent>
          </Dialog>

          {/* SCHEME CREDIT DIALOG */}
          <ApplySchemeCreditDialog
            open={schemeCreditDialogOpen}
            onClose={() => setSchemeCreditDialogOpen(false)}
            customerTokenNo={billData.tokenNo}
            billId={billId}
            billAmount={calculations.totalAmount}
            onSuccess={handleSchemeCreditApplied}
            previewMode={false}
          />

          {/* Decorative Fade */}
          <Box
            sx={{
              mt: 6,
              height: 60,
              background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.3))",
              borderRadius: 2,
            }}
          />
        </Box>
      </Box>
    </AppLayout>
  );
}
